import { injectable, inject } from 'inversify';
import { redirect } from 'next/navigation';
import ErrorCodeHelper, { TErrorKeys } from '@dealroadshow/error-codes';
import Dispatcher, { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import Container from '@framework/di/Container';
import isServer from '@framework/router/Next/isServer';
import AlertManager from '@dealroadshow/uikit-alert/AlertManager';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import Logger from '@framework/sentry/Logger';
import messages from '@framework/message/messages';
import { ConsentContextType, ConsentContextSymbol } from '@dataroom/consent/types';
import {
  AccessRestrictedContextType,
  AccessRestrictedContextSymbol,
} from '@dataroom/access-restricted/contexts/AccessRestrictedContext';
import {
  AccessRestrictedByIpContextType,
  AccessRestrictedByIpContextSymbol,
} from '@dataroom/access-restricted/contexts/AccessRestrictedByIpContext';
import { SelfDeclarationSeedSymbol, type ISelfDeclarationSeed } from '@dataroom/self-declaration/types';
import { getTwoFactorViolationDataroomName } from './getTwoFactorViolationDataroomName';
import dmPortalUrl from '@dm-portal/url';
import dataroomConfig from '@dataroom/config';
import { dataroomUrl } from '@dataroom/url';
import TenantConfig from '@framework/tenant/TenantConfig';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';

const mutedErrors: TErrorKeys[] = [
  'DATAROOM_TWO_FACTOR_VIOLATION',
  'DATAROOM_CONSENT_NOT_ACCEPTED',
  'DATAROOM_ACCESS_RESTRICTED',
  'DATAROOM_FILESYSTEM_ELEMENT_ACCESS_RESTRICTED',
  'DATAROOM_BUSINESS_COUNTRY_RESTRICTED',
  'DATAROOM_BUSINESS_COUNTRY_RESTRICTED_BY_IP',
  'PRIVATE_FILES_ACCESS_NOT_COMPLETED',
  'USER_NOT_AUTHENTICATED',
  'USER_AUTH_ERROR',
];

@injectable()
class DataroomErrorHandler {
  constructor(
    @inject(Container) private readonly container: Container,
  ) {}

  private redirectTo(url: string): void {
    if (isServer()) {
      redirect(url);
    } else {
      window.location.href = url;
    }
  }

  /**
   * Handles dataroom error.
   */
  handleError(
    error: unknown,
    defaultNotification: string | null = null,
    tenant: DataroomTenant | null = null,
    returnUrl: string | null = null,
  ): void {
    // No window on the server — the caller must pass `tenant` explicitly there (every
    // server-side caller already has it in scope via DataroomTenantContextSymbol).
    const tenantCode = tenant || (isServer() ? null : TenantConfig.fromHostname(window.location.hostname).code);

    if (!tenantCode) {
      Logger.error({ message: 'DataroomErrorHandler: tenant is required on the server', error });
      return;
    }

    const tenantConfig = (dataroomConfig as Record<string, { options?: Record<string, unknown> }>)[tenantCode];
    const isDataroomTenant = tenantConfig?.options?.isDataroom;
    const isGeneralAppDisclaimerEnabled = tenantConfig?.options?.isGeneralAppDisclaimerEnabled;

    if (Dispatcher.isBrowserAbortError(error)) {
      return;
    }

    if (!(error instanceof RpcError)) {
      Logger.error({ message: 'Dataroom response ErrorHandler scope. Not RpcError error.', error });
      return;
    }

    if (isServer() && error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_TWO_FACTOR_VIOLATION')) {
      const dataroomName = getTwoFactorViolationDataroomName(error);
      const twoFactorReturnUrl = returnUrl || dataroomUrl(tenantCode as DataroomTenant).getDataroomUrl(dataroomName);

      this.redirectTo(
        dataroomUrl(tenantCode as DataroomTenant).getDataroomListTwoFactorRedirectUrl(dataroomName, twoFactorReturnUrl),
      );
    }

    // Only records which dataroom triggered it — SelfDeclarationModal fetches the data it needs itself.
    if (isServer() && error.getCode() === ErrorCodeHelper.getCodeByName('PRIVATE_FILES_ACCESS_NOT_COMPLETED')) {
      const { dataroomId } = error.getError() as unknown as { dataroomId: number };

      this.container.bindContextValue(
        SelfDeclarationSeedSymbol,
        { dataroomId } satisfies ISelfDeclarationSeed,
        true,
      );
    }

    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_ACCESS_RESTRICTED')) {
      const dataroomDisplayName = error.getData()?.dataroomDisplayName;
      const dataroomTenant = tenant || tenantCode;

      if (isDataroomTenant) {
        this.redirectTo(dataroomUrl(dataroomTenant as DataroomTenant).getAccessRestrictedUrl(dataroomDisplayName));
      } else if (!isServer()) {
        const {
          showAccessRestrictedModal,
        } = this.container.get<AccessRestrictedContextType>(AccessRestrictedContextSymbol);
        showAccessRestrictedModal(dataroomDisplayName);
      }
    }

    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_BUSINESS_COUNTRY_RESTRICTED')) {
      const dataroomDisplayName = error.getData()?.dataroomDisplayName;
      const dataroomTenant = tenant || tenantCode;

      if (isDataroomTenant) {
        this.redirectTo(dataroomUrl(dataroomTenant as DataroomTenant).getAccessRestrictedUrl(dataroomDisplayName, false, true));
      } else if (!isServer()) {
        const {
          showAccessRestrictedModal,
        } = this.container.get<AccessRestrictedContextType>(AccessRestrictedContextSymbol);
        showAccessRestrictedModal(dataroomDisplayName, 'true');
      }
    }

    if (!isServer() && error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_FILESYSTEM_ELEMENT_ACCESS_RESTRICTED')) {
      const {
        showAccessRestrictedModal,
      } = this.container.get<AccessRestrictedContextType>(AccessRestrictedContextSymbol);
      showAccessRestrictedModal();
    }

    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_BUSINESS_COUNTRY_RESTRICTED_BY_IP')) {
      const dataroomName = error.getData()?.name;
      const ip = error.getData()?.ip;
      const countryId = error.getData()?.countryId;
      const dataroomTenant = tenant || tenantCode;

      if (isDataroomTenant) {
        this.redirectTo(
          dataroomUrl(dataroomTenant as DataroomTenant).getAccessRestrictedByIdUrl(dataroomName, ip, countryId),
        );
      } else if (!isServer()) {
        const { showAccessRestrictedByIpModal } = this.container.get<AccessRestrictedByIpContextType>(
          AccessRestrictedByIpContextSymbol,
        );
        showAccessRestrictedByIpModal(dataroomName, ip, countryId);
      }
    }

    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_BUSINESS_COUNTRY_NOT_SELECTED')) {
      this.redirectTo(dmPortalUrl.getUrl(`/${ dataroomConfig.researchroom.code }/my_researches`));
      return;
    }

    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_GENERAL_APPLICATION_DISCLAIMER_NOT_ACCEPTED')) {
      if (isGeneralAppDisclaimerEnabled) {
        this.redirectTo(dmPortalUrl.getUrl(`/${ dataroomConfig.researchroom.code }/my_researches`));
        return;
      }
    }

    // todo: check this when BE be ready
    // todo: go to direct links with disabled consent
    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_CONSENT_NOT_ACCEPTED')) {
      const veriSendSetsUrl = dmPortalUrl.getUrl(`/${ dataroomConfig.verisend.code }/my_sets`);
      const researchroomSetsUrl = dmPortalUrl.getUrl(`/${ dataroomConfig.researchroom.code }/my_researches`);

      if (tenantCode === dataroomConfig.verisend.code) {
        this.redirectTo(veriSendSetsUrl);
        return;
      }

      if (isGeneralAppDisclaimerEnabled) {
        this.redirectTo(researchroomSetsUrl);
        return;
      }

      if (!isServer()) {
        const { setIsConsentAccepted } = this.container.get<ConsentContextType>(ConsentContextSymbol);
        setIsConsentAccepted(false);
      }
    }

    if (error.getCode() === ErrorCodeHelper.getCodeByName('DATAROOM_USER_NOT_ADDED_TO_DATAROOM')) {
      if (
        tenant === dataroomConfig.verisend.code || tenant === dataroomConfig.investorset.code
        || tenant === dataroomConfig.researchroom.code
      ) {
        const setsName = tenant === dataroomConfig.researchroom.code ? 'explore_researches' : 'explore_sets';
        const name = encodeURIComponent(error.getData()?.dataroomDisplayName);

        this.redirectTo(dmPortalUrl.getUrl(`/${ tenant }/${ setsName }?search=${ name }`));
        return;
      }
    }

    const shouldErrorNotificationBeMuted = !!mutedErrors.find(
      (mutedError: TErrorKeys) => error?.getCode() === ErrorCodeHelper.getCodeByName(mutedError),
    );

    if (shouldErrorNotificationBeMuted) {
      return;
    }

    const notification = defaultNotification || getErrorMessage(error);

    // Same rule on both sides: what the client would show as the generic error is a real failure.
    if (notification === messages.GENERAL_ERROR) {
      Logger.error({ message: 'Dataroom response ErrorHandler scope.', error });
    }

    if (isServer()) {
      return;
    }

    AlertManager.error(notification);
  }
}

export default DataroomErrorHandler;
